import { Player, SavedTournament, StandingsRow } from "../types.ts";
import { parseCzechDate } from "./date-utils.ts";

/**
 * Výpočet průběžného pořadí šampionátu.
 *
 * Sdílí ho administrace i veřejná stránka – obě tak vždy ukazují
 * stejná čísla.
 */

/** Turnaje od nejnovějšího po nejstarší (datum je ve tvaru DD.MM.RRRR). */
export const sortTournamentsByDateDesc = (
  tournaments: SavedTournament[]
): SavedTournament[] =>
  [...tournaments].sort((a, b) => {
    const dateA = new Date(String(a.date).split(".").reverse().join("-"));
    const dateB = new Date(String(b.date).split(".").reverse().join("-"));
    return dateB.getTime() - dateA.getTime();
  });

/** Klub, pod kterým turnaje běží. */
export const CLUB_NAME = "Kuřim";

/**
 * Název sezony podle ročníku odehraných turnajů, ne podle "dneška" –
 * v lednu se tak nadpis nepřepne na nový ročník dřív, než se odehraje
 * první turnaj. Bez turnajů padá zpět na aktuální rok.
 *
 * @param tournaments turnaje seřazené od nejnovějšího
 */
export const getSeasonTitle = (tournaments: SavedTournament[]): string => {
  const newest = tournaments.find((t) => parseCzechDate(String(t.date)));
  const year = newest
    ? (parseCzechDate(String(newest.date)) as Date).getFullYear()
    : new Date().getFullYear();

  return `GP ${year} ${CLUB_NAME}`;
};

/** Počet hráčů, kteří se turnaje zúčastnili. */
export const getParticipantCount = (tournament: SavedTournament): number =>
  tournament.playerCount || (tournament.player ? tournament.player.length : 0);

/** Umístění hráče v turnaji (1 = vítěz), null když nehrál. */
export const getPlayerRank = (
  tournament: SavedTournament,
  playerId: string
): number | null => {
  const index = (tournament.player || []).indexOf(playerId);
  if (index < 0) return null;
  const rank = tournament.rankings ? tournament.rankings[index] : undefined;
  return typeof rank === "number" && !isNaN(rank) ? rank : null;
};

/** Jméno vítěze turnaje (z uloženého turnaje, jinak dohledané u hráčů). */
export const getWinnerName = (
  tournament: SavedTournament,
  players: Player[]
): string | null => {
  const count = getParticipantCount(tournament);
  for (let i = 0; i < count; i++) {
    const rank = tournament.rankings ? tournament.rankings[i] : undefined;
    if (Number(rank) === 1) {
      const saved = tournament.playerNames?.[i];
      if (saved) return saved;
      const id = tournament.player?.[i];
      const player = players.find((p) => p.id === id);
      return player?.player_name || null;
    }
  }
  return null;
};

/**
 * Sestaví pořadí všech hráčů.
 *
 * @param players     všichni hráči
 * @param tournaments turnaje seřazené od nejnovějšího (viz sortTournamentsByDateDesc)
 */
export const buildStandings = (
  players: Player[],
  tournaments: SavedTournament[]
): StandingsRow[] => {
  const rows = players.map((player) => {
    const results: (number | null)[] = [];
    let totalPoints = 0;
    let placementPoints = 0;
    let attended = 0;
    let wins = 0;
    let bestRank: number | null = null;

    tournaments.forEach((tournament) => {
      const index = (tournament.player || []).indexOf(player.id);

      if (index < 0) {
        results.push(null);
        return;
      }

      attended++;

      const participants = getParticipantCount(tournament);
      const rank = tournament.rankings
        ? Number(tournament.rankings[index])
        : NaN;

      // Body za turnaj podle umístění: vítěz bere tolik, kolik bylo hráčů
      const placement = isNaN(rank) ? null : participants + 1 - rank;
      results.push(placement);
      if (placement !== null) placementPoints += placement;

      if (!isNaN(rank)) {
        if (rank === 1) wins++;
        if (bestRank === null || rank < bestRank) bestRank = rank;
      }

      const points = tournament.points ? tournament.points[index] : undefined;
      if (typeof points === "number" && !isNaN(points)) {
        totalPoints += points;
      }
    });

    return {
      playerId: player.id,
      playerName: player.player_name,
      totalPoints,
      placementPoints,
      results,
      attended,
      attendance: tournaments.length
        ? Math.round((attended / tournaments.length) * 100)
        : 0,
      pointsPerVisit: attended ? placementPoints / attended : 0,
      wins,
      bestRank,
    };
  });

  // Pořadí se řídí body za umístění – ta jsou odolná vůči tomu, že malý
  // nebo dvoukolový turnaj rozdá víc bodů z partií na hlavu.
  return rows.sort((a, b) => {
    if (b.placementPoints !== a.placementPoints) {
      return b.placementPoints - a.placementPoints;
    }
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.playerName.localeCompare(b.playerName, "cs");
  });
};

/** Souhrnná čísla pro veřejnou stránku. */
export const getChampionshipSummary = (
  tournaments: SavedTournament[],
  standings: StandingsRow[]
) => {
  const activePlayers = standings.filter((row) => row.attended > 0);

  const gamesPlayed = tournaments.reduce((sum, tournament) => {
    const count = getParticipantCount(tournament);
    // Každý s každým: n*(n-1)/2, dvoukolově dvakrát tolik
    const games = (count * (count - 1)) / 2;
    return sum + (tournament.doubleRound ? games * 2 : games);
  }, 0);

  return {
    tournamentCount: tournaments.length,
    playerCount: activePlayers.length,
    gamesPlayed,
    leader: activePlayers[0] || null,
  };
};
